import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCRUD = ({ 
  title, 
  endpoint, 
  fields, 
  columns,
  renderPreview 
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [endpoint]);

  const fetchItems = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/${endpoint}`);
      const data = await res.json();
      setItems(data);
    } catch (err) {
      console.error('Error fetching items:', err);
    } finally {
      setLoading(false);
    }
  };

  const initFormData = () => {
    const initial = {};
    fields.forEach(field => {
      if (field.type === 'array') {
        initial[field.name] = field.default || [];
      } else if (field.type === 'boolean') {
        initial[field.name] = field.default !== undefined ? field.default : true;
      } else if (field.type === 'number') {
        initial[field.name] = field.default || 0;
      } else {
        initial[field.name] = field.default || '';
      }
    });
    return initial;
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(initFormData());
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const data = {};
    fields.forEach(field => {
      data[field.name] = item[field.name] !== undefined ? item[field.name] : (field.type === 'array' ? [] : '');
    });
    setFormData(data);
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/${endpoint}/${item.id}`, {
        method: 'DELETE'
      });
      fetchItems();
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingItem 
        ? `${API_URL}/api/admin/${endpoint}/${editingItem.id}`
        : `${API_URL}/api/admin/${endpoint}`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      setShowForm(false);
      fetchItems();
    } catch (err) {
      console.error('Error saving item:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (name, value) => {
    const arr = value.split('\n').filter(v => v.trim());
    setFormData(prev => ({ ...prev, [name]: arr }));
  };

  const renderField = (field) => {
    const value = formData[field.name];

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            name={field.name}
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
          />
        );
      case 'array':
        return (
          <textarea
            name={field.name}
            value={Array.isArray(value) ? value.join('\n') : ''}
            onChange={(e) => handleArrayChange(field.name, e.target.value)}
            placeholder={field.placeholder || 'One item per line'}
            rows={4}
          />
        );
      case 'boolean':
        return (
          <label className="checkbox-label">
            <input
              type="checkbox"
              name={field.name}
              checked={value || false}
              onChange={handleChange}
            />
            <span>{field.checkboxLabel || 'Active'}</span>
          </label>
        );
      case 'number':
        return (
          <input
            type="number"
            name={field.name}
            value={value || 0}
            onChange={handleChange}
            required={field.required}
            min={field.min}
            max={field.max}
          />
        );
      case 'select':
        return (
          <select
            name={field.name}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type={field.type || 'text'}
            name={field.name}
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            required={field.required}
          />
        );
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="admin-crud">
      <div className="admin-page-header">
        <div>
          <h1>{title}</h1>
          <p>{items.length} items</p>
        </div>
        <button onClick={handleAdd} className="admin-btn primary">
          <i className="fa-solid fa-plus"></i>
          Add New
        </button>
      </div>

      {/* Items Table */}
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                {columns.map(col => (
                  <td key={col.key}>
                    {col.render ? col.render(item[col.key], item) : item[col.key]}
                  </td>
                ))}
                <td className="actions-cell">
                  <button onClick={() => handleEdit(item)} className="action-btn edit">
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <button onClick={() => handleDelete(item)} className="action-btn delete">
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="admin-empty">
            <i className="fa-solid fa-inbox"></i>
            <p>No items found</p>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="admin-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingItem ? 'Edit' : 'Add'} {title.replace(/s$/, '')}</h2>
              <button onClick={() => setShowForm(false)} className="modal-close">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-grid">
                {fields.map(field => (
                  <div key={field.name} className={`form-group ${field.fullWidth ? 'full-width' : ''}`}>
                    <label>{field.label}</label>
                    {renderField(field)}
                  </div>
                ))}
              </div>

              {renderPreview && (formData.image || formData.url) && (
                <div className="form-preview">
                  <label>Preview</label>
                  <img src={formData.image || formData.url} alt="Preview" />
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)} className="admin-btn secondary">
                  Cancel
                </button>
                <button type="submit" className="admin-btn primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCRUD;
